package com.neurobs.ccabserver.transcriptreview.states

import com.neurobs.react.{GenericState, GenericStateGenerator, RegisteredChangeAttribute, StateAttribute}
import com.neurobs.ccabserver.transcriptreview.util.{EditableTranscriptWord, RecordingTranscriptReviewHandler}
import com.neurobs.util.TimeSeconds
import org.scalajs.dom
import org.scalajs.dom.console
import slinky.core.{FunctionalComponent, TagElement}
import slinky.core.annotations.react
import slinky.core.facade.{ReactElement, ReactRef}
import slinky.core.facade.Hooks._
import slinky.core.facade.React.createRef
import slinky.web.SyntheticMouseEvent

import scala.scalajs.js

class WordEditingHandler(mouseDownX: Double, handlePixelDeltaAndDisplacement: Double => Unit, editSide: Either[String, String], zoom: Double) {

	def editingLeft = editSide.isLeft
	def editingRight = editSide.isRight

	val initialX = mouseDownX/zoom
	var mostRecentX = mouseDownX/zoom

	def process(event: SyntheticMouseEvent[TagElement#RefType]) = {
		val eventX = event.clientX/zoom
		val pixelDelta = eventX - mostRecentX
		mostRecentX = eventX
		handlePixelDeltaAndDisplacement(pixelDelta)
	}

	def totalDisplacement = mostRecentX - initialX
}

case class EditableWordState
(
	editableWord: EditableTranscriptWord,
	recordingState: RecordingTranscriptReviewState,
	testState: TestTranscriptReviewState,
	divPositionRef: ReactRef[dom.html.Div],
	inputRef: ReactRef[dom.html.Input],
	wordEditingHandler: StateAttribute[Option[WordEditingHandler]],
	cursorOnEdge: StateAttribute[Boolean],
	registeredChange: RegisteredChangeAttribute
) extends GenericState {

	import EditableWordState._

	def recordingReviewHandler = recordingState.handler
	def positionRef: ReactRef[dom.html.Div] = divPositionRef
	def boundingLeft: Double = positionRef.current.getBoundingClientRect().left
	def boundingRight: Double = positionRef.current.getBoundingClientRect().right

	def indexOfWord: Option[Int] = recordingReviewHandler.indexOfWord(editableWord)

	def registerChange() = registeredChange.register()

	def isFocused = recordingState.focusedWord.value == editableWord
	def setAsFocused() = recordingState.setFocusedWord(editableWord)

	def isHighlighted = recordingState.highlightedWord.value.map(_ == editableWord).getOrElse(false)

	def styleAttributes: Seq[(String, js.Any)] = {
		val attributes = Seq(
			("marginLeft", calculateLeftMargin + "px"),
			("marginRight", calculateMarginRight + "px"),
			("width", calculateWidth + "px"),
			("zIndex", if (isFocused) "100" else "5"),
			("cursor", if (cursorOnEdge.value) "col-resize" else "default"),
			("position", "relative"),
			("display", "inline-block"),
			("borderColor", "white"),
			("borderStyle", "inset"),
			("textAlign", "center"),
		) ++ (if (isHighlighted) Seq(("boxShadow", "0px 0px 20px 10px white")) else Seq())

		attributes.asInstanceOf[Seq[(String, js.Any)]]
	}

	def editContent(newWordString: String) = {
		editableWord.word = newWordString
		testState.registerChange()
	}

	private def setStartTime(newStartTime: TimeSeconds) = {editableWord.startTime = newStartTime}
	private def setEndTime(newEndTime: TimeSeconds) = {editableWord.endTime = newEndTime}

	def adjustStartTime(pixelDelta: Double) = {
		val secondsDelta = testState.pixelsToSeconds(pixelDelta)
		setStartTime(editableWord.startTime + secondsDelta)
		registerChange()
	}

	def adjustEndTime(pixelDelta: Double) = {
		val secondsDelta = testState.pixelsToSeconds(pixelDelta)
		setEndTime(editableWord.endTime + secondsDelta)
		registerChange()
	}

	def isFlagged = editableWord.word.startsWith(FLAG_STR) && editableWord.word.endsWith(FLAG_STR)
	def flag() = {
		if (isFlagged) {
			editableWord.word = editableWord.word.drop(1).dropRight(1)
		} else {
			editableWord.word = s"%${editableWord.word}%"
		}
		testState.registerChange()
	}

	def removeWord() = {
		recordingReviewHandler.removeWord(editableWord)
		testState.registerChange()
	}

	def calculateWidth = testState.secondsToPixels(editableWord.duration.abs)
	def calculateLeftMargin = recordingState.calculateLeftMargin(editableWord, testState.PPS)
	def calculateMarginRight = wordEditingHandler.value match {
		case None => 0
		case Some(handler) =>
			if (handler.editingLeft) 0
			else {
				-handler.totalDisplacement
			}
	}

	def eventOnLeftEdge(event: SyntheticMouseEvent[TagElement#RefType]): Boolean = {
		val edgeDistance = event.clientX - boundingLeft
		edgeDistance > -10 && edgeDistance < 20
	}

	def eventOnRightEdge(event: SyntheticMouseEvent[TagElement#RefType]): Boolean = {
		val edgeDistance = boundingRight - event.clientX
		edgeDistance > 0 && edgeDistance < 30
	}

	def eventOnEdge(event: SyntheticMouseEvent[TagElement#RefType]): Boolean = {
		eventOnLeftEdge(event) || eventOnRightEdge(event)
	}

	def createStartTimeEditHandler(initialX: Double) = new WordEditingHandler(initialX, adjustStartTime, Left("start"), testState.getZoomScale)
	def createEndTimeEditHandler(initialX: Double) = new WordEditingHandler(initialX, adjustEndTime, Right("end"), testState.getZoomScale)

	def handleMouseDown(event: SyntheticMouseEvent[TagElement#RefType]) = {

		if (eventOnLeftEdge(event)) {
			setOptionState(wordEditingHandler, createStartTimeEditHandler(event.clientX))
		}
		else if (eventOnRightEdge(event)) {
			setOptionState(wordEditingHandler, createEndTimeEditHandler(event.clientX))
		}
	}

	def handleMouseMove(event: SyntheticMouseEvent[TagElement#RefType]) = {
		setState(cursorOnEdge, eventOnEdge(event))
		wordEditingHandler.value.map(handler => handler.process(event))
		registerChange()
	}

	def handleMouseUp() = {
		recordingState.registerChange()
		voidState(wordEditingHandler)
	}

	def handleMouseEnter() = {
		setAsFocused()
	}

	def handleMouseOut() = {
		if (wordEditingHandler.value.map(_.editingRight).getOrElse(false)) {
			recordingState.registerChange()
		}
		voidState(wordEditingHandler)
	}
}

object EditableWordState {
	val FLAG_STR = "%"
}

object EditableWordStateGenerator extends GenericStateGenerator[EditableWordState] {

	override def generateEmptyDefaultState(): EditableWordState = {
		EditableWordState(
			EditableTranscriptWord(),
			RecordingTranscriptReviewStateGenerator.generateEmptyDefaultState(),
			TestTranscriptReviewStateGenerator.generateEmptyDefaultState(),
			createRef[dom.html.Div],
			createRef[dom.html.Input],
			emptyOptionDefaultState(),
			emptyDefaultState(false),
			emptyRegisteredChangeState()
		)
	}
}

@react
object EditableWordStateProvider {

	case class Props(editableWord: EditableTranscriptWord, children: ReactElement*)

	val component = FunctionalComponent[Props] { props =>

		val recordingState = RecordingTranscriptReviewStateGenerator.accessState
		val testState = TestTranscriptReviewStateGenerator.accessState


		val wordEditingHandlerState = StateAttribute(useState[Option[WordEditingHandler]](None))
		val cursorOnEdgeState = StateAttribute(useState(false))
		val registeredChangeState = RegisteredChangeAttribute(useState(0))

		val divPositionRef = createRef[dom.html.Div]
		val inputRef = createRef[dom.html.Input]

		val wordReviewState = {
			EditableWordState(
				props.editableWord,
				recordingState,
				testState,
				divPositionRef,
				inputRef,
				wordEditingHandlerState,
				cursorOnEdgeState,
				registeredChangeState
			)
		}

		EditableWordStateGenerator.Provider(wordReviewState)(
			props.children:_*
		)
	}
}